import React from 'react';
import PropTypes from 'prop-types';
import { values, result } from 'underscore';
import { CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { sharedComponentFunctionStateStore } from '@bingads-webui-campaign/recommendation-core';
import campaignCacheManager from '@bingads-webui-campaign-legacy/cache-manager/index';
import { SummaryBarFactory } from './components';
import { campaignEntityItems, showErrorWithAlertCenter } from './helpers';
import { convertToSummaryBarStatusPatch } from './helpers/entity-converter';

export const CampaignSummaryBar = ({
  campaign,
  osData,
  i18n,
  updateCampaign,
  canEdit,
  scope,
  siteMap,
  permissions,
  skipInvalidateCache,
  summaryBarActivityLogger,
  excludedLocationOnly,
}) => {
  const changeStatus = selectedStatus => updateCampaign({
    campaign,
    patch: ({ status: selectedStatus.id }),
    converter: convertToSummaryBarStatusPatch,
  }).catch((response) => {
    showErrorWithAlertCenter(response, i18n);
  });
  const updateInlineBudget = patch => updateCampaign({
    campaign,
    patch,
    converter: item => ({ Budget: item.budget }),
  });
  const updateSharedBudget = patch => updateCampaign({
    campaign,
    patch,
    updateLevel: 'Budget',
    budgetId: campaign.budget.Id,
  }).then(() => {
    // update shared budget can't be detected by campaign odata cache manager
    // need to do it manually
    // skipInvalidateCache is for the test
    if (!skipInvalidateCache) {
      campaignCacheManager.invalidate('Campaign');
    }
  });

  const campaignSummaryBarItems = campaignEntityItems({
    changeStatus, scope, siteMap, permissions, i18n, ...campaign, osData, canEdit, updateInlineBudget, updateSharedBudget, summaryBarActivityLogger, excludedLocationOnly,
  });

  sharedComponentFunctionStateStore.setState({ updateCampaignStatus: changeStatus });

  const { id, name, campaignType } = campaign;

  const summaryBarProps = {
    breadcrumbsProps: { campaignName: name, campaignId: id, campaignType },
    summaryBarItems: campaignSummaryBarItems,
    i18n,
    summaryBarActivityLogger,
    scope,
    siteMap,
  };

  const isAppBarBreadcrumbEnabled = result(permissions.dynamic, 'AppBarBreadcrumb', false);
  return (<SummaryBarFactory {...summaryBarProps} hideBreadcrumb={isAppBarBreadcrumbEnabled} />);
};

CampaignSummaryBar.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    campaignType: PropTypes.oneOf(values(CampaignType)).isRequired,
    currentCampaignType: PropTypes.oneOf(values(CampaignType)).isRequired,
    campaignSubType: PropTypes.string,
    budget: PropTypes.shape({}).isRequired,
    bidStrategy: PropTypes.string.isRequired,
    locations: PropTypes.array,
    audiences: PropTypes.array,
    devices: PropTypes.array,
    hasBudgetLandscape: PropTypes.bool,
  }).isRequired,
  osData: PropTypes.shape({
    OverallOptimizationScoreBar: PropTypes.exact({
      Available: PropTypes.number,
      Dismissed: PropTypes.number,
      Gained: PropTypes.number,
    }),
  }).isRequired,
  excludedLocationOnly: PropTypes.bool.isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  siteMap: siteMapPropType.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  updateCampaign: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  summaryBarActivityLogger: PropTypes.shape({
    changeScopeActivity: PropTypes.any,
  }).isRequired,
  skipInvalidateCache: PropTypes.bool.isRequired,
};
